import { isEqual } from 'lodash';
import { bool, func } from 'prop-types';
import React from 'react';
import { Col, FormGroup, Input, Label, Row } from 'reactstrap';

import Association from '../../associations/Association';
import { CampaignLink } from '../../campaigns/Link';
import CurrencyFormat from '../../generic/CurrencyFormat';
import DateRange from '../../generic/DateRange';
import MandatoryTextForFields from '../../generic/MandatoryTextForFields';
import SingleSelect from '../../generic/SingleSelect';
import TagInput from '../../generic/TagInput';
import getInfoTooltip from '../../generic/Tooltip/get-info-tooltip';
import { isPC } from '../../layout/Variant/Variant';
import {
  brandCodeFieldText,
  businessModels,
  campaignTypeOptions,
  creativePoolOptions,
  useCasesOptions,
} from '../../opportunities/constants';
import DefaultCopyAssetsWarning from '../../opportunities/DefaultCopyAssetsWarning';
import HELP_TEXTS from '../../opportunities/help-texts';
import OpportunityCampaignObjective from '../../opportunities/OpportunityForm/OpportunityCampaignObjective';
import OpportunitySalesUnit from '../../opportunities/OpportunityForm/OpportunitySalesUnit';
import { opportunityShape } from '../../prop-types';

function ProjectHeaderForm({ opportunity, onChange, isEditing }) {
  const {
    accountSfId,
    campaignStart,
    campaignEnd,
    expectedRevenue,
    ownerId,
    businessModel,
    useCase,
    brandCodes,
    comment,
    name,
    campaignType,
    collaboratorIds,
    campaignId,
    creativePool,
  } = opportunity;
  const inputProps = { readOnly: !isEditing, onChange };

  const [brandIds] = React.useState(opportunity.brandIds);

  const brandsHaveChanged = !isEqual(brandIds, opportunity.brandIds);

  return (
    <Row>
      <Col>
        <FormGroup row>
          <Label sm={4} for="op-name" className="required">
            Name
          </Label>
          <Col sm={8}>
            <Input
              id="op-name"
              name="name"
              value={name || ''}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="op-accountSfId" className="required">
            Account ID
          </Label>
          <Col sm={8}>
            <Input
              id="op-accountSfId"
              name="accountSfId"
              value={accountSfId || ''}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="op-businessModel">
            Business Model
          </Label>
          <Col sm={8}>
            <SingleSelect
              options={businessModels}
              id="op-businessModel"
              name="businessModel"
              value={businessModel}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="op-brandIds" className="required">
            Brands
          </Label>
          <Col sm={8}>
            <Association
              id="op-brandIds"
              name="brandIds"
              value={brandIds}
              type="brand"
              isMulti
              verbose
              {...inputProps}
            />
          </Col>
          {campaignId && (
            <DefaultCopyAssetsWarning
              campaignId={campaignId}
              isEditing={isEditing}
              brandIds={brandIds}
              brandsHaveChanged={brandsHaveChanged}
            />
          )}
        </FormGroup>
        {isPC() && isEditing && (
          <FormGroup row>
            <Label sm={4} for="op-brandCodes">
              Brands
              {getInfoTooltip(HELP_TEXTS, 'primaryBrand', 'info')}
              <MandatoryTextForFields text={brandCodeFieldText} />
            </Label>
            <Col sm={8}>
              <TagInput
                id="op-brandCodes"
                name="brandCodes"
                value={brandCodes || []}
                {...inputProps}
              />
            </Col>
          </FormGroup>
        )}
        <FormGroup row>
          <Col className="col-form-label" sm={4}>
            Campaign
          </Col>
          <Col sm={8}>
            <span className="form-control-plaintext-without-color-override">
              {campaignId ? (
                <CampaignLink id={campaignId} />
              ) : (
                'A campaign for this opportunity has not been created yet'
              )}
            </span>
          </Col>
        </FormGroup>
        <OpportunityCampaignObjective
          opportunity={opportunity}
          {...inputProps}
        />
        <FormGroup row>
          <Label sm={4} for="op-comment">
            Description
          </Label>
          <Col sm={8}>
            <Input
              type="textarea"
              id="op-comment"
              name="comment"
              value={comment}
              {...inputProps}
            />
          </Col>
        </FormGroup>
      </Col>
      <Col>
        <OpportunitySalesUnit opportunity={opportunity} {...inputProps} />
        <FormGroup row>
          <Label sm={4} for="op-creativePool" className="required">
            Creative Team
          </Label>
          <Col sm={8}>
            <SingleSelect
              options={creativePoolOptions}
              id="op-creativePool"
              name="creativePool"
              value={creativePool}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="op-ownerId" className="required">
            Owner
          </Label>
          <Col sm={8}>
            <Association
              id="op-ownerId"
              name="ownerId"
              value={ownerId}
              type="user"
              {...inputProps}
              showIcon
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="op-collaboratorIds">
            Collaborators
          </Label>
          <Col sm={8}>
            <Association
              id="op-collaboratorIds"
              name="collaboratorIds"
              value={collaboratorIds}
              type="user"
              isMulti
              showTeam
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="op-useCase" className="required">
            Use Case
          </Label>
          <Col sm={8}>
            <SingleSelect
              options={useCasesOptions}
              id="op-useCase"
              name="useCase"
              value={useCase}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="op-campaignType" className="required">
            Campaign Type
          </Label>
          <Col sm={8}>
            <SingleSelect
              options={campaignTypeOptions}
              id="op-campaignType"
              name="campaignType"
              value={campaignType}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label
            sm={4}
            for="op-campaignDates-campaignStart"
            className="required"
          >
            Timeframe
          </Label>
          <Col sm={8}>
            <DateRange
              id="op-campaignDates"
              startName="campaignStart"
              endName="campaignEnd"
              startDate={campaignStart || null}
              endDate={campaignEnd || null}
              small
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="op-expectedRevenue">
            Budget
          </Label>
          <Col sm={8}>
            <CurrencyFormat
              id="op-expectedRevenue"
              className="form-control-plaintext"
              name="expectedRevenue"
              value={expectedRevenue}
              text
            />
          </Col>
        </FormGroup>
      </Col>
    </Row>
  );
}

ProjectHeaderForm.propTypes = {
  opportunity: opportunityShape.isRequired,
  onChange: func.isRequired,
  isEditing: bool.isRequired,
};

export default ProjectHeaderForm;
